"use client";

import NumberOptionList from "@/app/components/numberOptionList";
import useAnalyticsManager from "@/app/hooks/useAnalyticsManager";
import { useAppDispatch } from "@/store/hooks";
import { useGetShoppingCartQuery, useModifyShoppingCartMutation } from "@/store/shoppingCartApi";
import { showCartToast } from "@/store/shoppingCartSlice";
import { DeviceType } from "@/types/search";
import getTrueCategoryFromAlgoliaCategoryList from "@/utils/analytics/getTrueCategoryFromAlgoliaCategoryList";
import { IncomingAnalyticsProduct } from "@/utils/analyticsEventSender";
import { StatusCodes } from "http-status-codes";
// import { SendEventForHits } from "instantsearch.js/es/lib/utils";
import Button from "@/components/design-system/button";
import { SearchTypes } from "@/declarations/search";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import style from "./productsCard.module.scss";

type AddToCartProps = {
  isListView: boolean;
  deviceType: DeviceType;
  // hit: ProductHit;
  // sendEvent: SendEventForHits;
  minQty: number;
  vendorProductId: number;
  inventory: number;
  queryId?: string;
  position: number;
  isSponsoredProduct: boolean;
  resolvedBidId?: string;
} & Pick<SearchTypes.ProductHit, "mp_id" | "category" | "title" | "price" | "brand">;

export default function AddToCart({
  isListView,
  deviceType,
  mp_id,
  category,
  title,
  price,
  brand,
  minQty,
  vendorProductId,
  inventory,
  queryId,
  isSponsoredProduct,
  resolvedBidId,
}: AddToCartProps) {
  const [modifyShoppingCartMutation] = useModifyShoppingCartMutation();
  const { dispatchModifyCartEvent } = useAnalyticsManager();
  const dispatch = useAppDispatch();

  const [quantity, setQuantity] = useState(1);
  const [quantityChangedByUser, setQuantityChangedByUser] = useState(false);
  const categoryName = useMemo(() => {
    const categoryLevels = Object.values(category);
    const splitCategory = categoryLevels[categoryLevels.length - 1].split(" > ");
    return splitCategory[splitCategory.length - 1];
  }, [category]);

  const { data: shoppingCart } = useGetShoppingCartQuery();
  const productInCart = useMemo(() => {
    const products = !shoppingCart
      ? []
      : shoppingCart.vendorOrders
          .flatMap((vo) => {
            return vo.products;
          })
          .filter((p) => !p.noLongerForSale);
    return products.find((p) => p.mpId === mp_id && p.vendorProductId === vendorProductId);
  }, [shoppingCart, mp_id, vendorProductId]);

  useEffect(() => {
    if (productInCart && quantityChangedByUser === false) {
      setQuantity(productInCart?.quantity);
    }
  }, [productInCart, quantityChangedByUser]);

  const [productIsOrWillBeAddedToCart, setProductIsOrWillBeAddedToCart] = useState(false);

  const modifyShoppingCartMutationWrapper = useCallback(
    async (event?: FormEvent<HTMLFormElement>, removeFromCart?: boolean) => {
      if (event) {
        event.preventDefault();
      }
      if (!removeFromCart) {
        setProductIsOrWillBeAddedToCart(true);
      }
      const quantityCurrentlyInCart = productInCart ? productInCart.quantity : 0;
      const newQuantity = removeFromCart ? 0 : quantity;
      const response = await modifyShoppingCartMutation({
        productsToModify: [
          {
            mpId: mp_id,
            vendorProductId: vendorProductId,
            minimumQuantity: minQty,
            quantity: newQuantity,
            categoryName: categoryName,
          },
        ],
        incrementQuantity: false,
      }).unwrap();
      if (response.status === StatusCodes.OK) {
        if (!productInCart && newQuantity > 0) {
          dispatch(
            showCartToast({
              message: "Item Added to Cart",
              subMessage: `"${title}" has been added to your cart.`,
              type: "success",
            }),
          );
        } else if (newQuantity === 0 && quantityCurrentlyInCart > 0) {
          dispatch(
            showCartToast({
              message: `${quantityCurrentlyInCart} item${quantityCurrentlyInCart > 1 ? "s" : ""} removed from your cart`,
              type: "success",
              showCheckoutLink: false,
            }),
          );
        }
        const productAddedToCart: IncomingAnalyticsProduct = {
          mpId: mp_id,
          productTitle: title,
          unitPrice: price,
          quantity: quantity,
          originalQuantity: productInCart ? productInCart.quantity : 0,
          brandName: brand,
          categoryList: [],
        };
        // this is for typescript...
        if (productAddedToCart.categoryList) {
          if (category["lvl1"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(category["lvl1"]));
          }
          if (category["lvl2"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(category["lvl2"]));
          }
          if (category["lvl3"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(category["lvl3"]));
          }
          if (category["lvl4"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(category["lvl4"]));
          }
          if (category["lvl5"]) {
            productAddedToCart.categoryList.push(getTrueCategoryFromAlgoliaCategoryList(category["lvl5"]));
          }
        }

        dispatchModifyCartEvent([productAddedToCart], "Search Page", isSponsoredProduct ? undefined : queryId);
      }

      // TODO - Failure condition
      // dispatch(showCartToast(`${quantity} item${quantity > 1 ? "s" : ""} added to cart`));
    },
    [
      brand,
      category,
      categoryName,
      dispatch,
      dispatchModifyCartEvent,
      minQty,
      modifyShoppingCartMutation,
      mp_id,
      price,
      productInCart,
      quantity,
      queryId,
      title,
      vendorProductId,
      isSponsoredProduct,
    ],
  );
  const debouncedModifyShoppingCartMutation = useDebouncedCallback(modifyShoppingCartMutationWrapper, 500);

  const increaseQuantity = useCallback(() => {
    if (debouncedModifyShoppingCartMutation.isPending()) {
      debouncedModifyShoppingCartMutation.cancel();
    }
    setQuantity(quantity + 1);
    debouncedModifyShoppingCartMutation();
    setQuantityChangedByUser(true);
  }, [debouncedModifyShoppingCartMutation, quantity]);

  const decreaseQuantity = useCallback(async () => {
    if (debouncedModifyShoppingCartMutation.isPending()) {
      debouncedModifyShoppingCartMutation.cancel();
    }
    if (quantity <= minQty) {
      setProductIsOrWillBeAddedToCart(false);
      modifyShoppingCartMutationWrapper(undefined, true);
      setQuantity(minQty);
    } else {
      setQuantity(quantity - 1);
      debouncedModifyShoppingCartMutation();
    }
    setQuantityChangedByUser(true);
  }, [debouncedModifyShoppingCartMutation, modifyShoppingCartMutationWrapper, quantity, minQty]);

  const [numberListStartingPoint, setNumberListStartingPoint] = useState(1);
  const [numberListEndingPoint, setNumberListEndingPoint] = useState(1);

  const onQuantityDropdownHover = useCallback(() => {
    setNumberListStartingPoint(minQty);
    setNumberListEndingPoint(inventory > 999 ? 999 : inventory);
  }, [inventory, minQty]);

  useEffect(() => {
    setProductIsOrWillBeAddedToCart(productInCart ? true : false);
  }, [productInCart]);

  const topsortClickEventAttributes = useMemo(() => {
    if (isSponsoredProduct) {
      return {
        "data-ts-resolved-bid": resolvedBidId,
        "data-ts-product": mp_id,
      };
    }
    return {};
  }, [isSponsoredProduct, mp_id, resolvedBidId]);

  if (!productIsOrWillBeAddedToCart) {
    return (
      <form
        className={`${style.addToCartForm} ${isListView && deviceType === "desktop" ? style.cartList : ""}`}
        onSubmit={modifyShoppingCartMutationWrapper}
      >
        <select
          id="productQuantity"
          aria-label="quantity"
          className={style.quantity}
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
          onMouseOver={onQuantityDropdownHover}
          {...topsortClickEventAttributes}
        >
          <NumberOptionList start={numberListStartingPoint} end={numberListEndingPoint} />
        </select>
        <Button
          variant="secondary"
          color="blue"
          text="Add to Cart"
          type="submit"
          showLeftIcon={false}
          showRightIcon={false}
          {...topsortClickEventAttributes}
        />
      </form>
    );
  } else {
    return (
      <div className={style.addRemoveButtonContainer}>
        <button
          className={style.addRemoveButton}
          onClick={decreaseQuantity}
          aria-label="Decrease quantity"
          {...topsortClickEventAttributes}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <span className={style.quantityDisplay}>{quantity}</span>
        <button
          disabled={quantity >= inventory}
          className={style.addRemoveButton}
          onClick={increaseQuantity}
          aria-label="Increase quantity"
          {...topsortClickEventAttributes}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    );
  }
}
